/**
 * TokenDebugOverlay.jsx — Phase 12a (2026-02).
 *
 * QoL debug view. When the URL contains `?debug=tokens`, mounts a
 * fixed-position panel (top-right) that dumps every `--*` custom
 * property currently resolved on `document.documentElement`, alongside
 * its computed value and a color-swatch preview when the value parses
 * as a color literal. Query-param-gated only (NOT env-gated) — opt-in.
 *
 * Dismiss: click the header X, or press Esc (top-of-stack — the
 * overlay `stopPropagation()`s its Esc so drawers/modals under it are
 * unaffected).
 *
 * Zero side effects, zero backend, zero new theme tokens.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

const _COLOR_LIKE = /^(?:#[0-9a-fA-F]{3,8}|rgba?\(|hsla?\()/;

function useQueryParam(name) {
  const [val, setVal] = useState(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get(name);
  });
  useEffect(() => {
    const onPop = () =>
      setVal(new URLSearchParams(window.location.search).get(name));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [name]);
  return val;
}

function collectTokens() {
  if (typeof window === 'undefined') return [];
  const cs = getComputedStyle(document.documentElement);
  // getComputedStyle exposes CSS custom properties on the CSSStyleDeclaration
  // interface — iterate the [i]-indexed shape rather than Object.entries.
  const out = [];
  for (let i = 0; i < cs.length; i++) {
    const name = cs[i];
    if (!name.startsWith('--')) continue;
    const value = cs.getPropertyValue(name).trim();
    out.push({ name, value });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

export default function TokenDebugOverlay() {
  const debugParam = useQueryParam('debug');
  const active = debugParam === 'tokens';
  const [dismissed, setDismissed] = useState(false);
  const tokens = useMemo(() => (active ? collectTokens() : []), [active]);

  useEffect(() => {
    if (!active || dismissed) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Top-of-stack semantics: swallow so drawers/modals below don't
        // also react. Safe because the overlay is always the outermost
        // consumer when mounted.
        e.stopPropagation();
        setDismissed(true);
      }
    };
    // Capture-phase so we run before any drawer/modal window listener.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active, dismissed]);

  if (!active || dismissed) return null;

  return (
    <aside
      data-testid="token-debug-overlay"
      style={{
        position: 'fixed',
        top: 'var(--sp-3, 12px)',
        right: 'var(--sp-3, 12px)',
        width: 360,
        maxHeight: '80vh',
        overflowY: 'auto',
        zIndex: 9999,
        background: 'var(--color-panel)',
        color: 'var(--color-text)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md, 6px)',
        padding: 'var(--sp-3, 12px)',
        fontFamily: 'var(--font-sans, sans-serif)',
        fontSize: 12,
        boxShadow: '0 12px 32px rgba(0,0,0,.35)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          marginBottom: 'var(--sp-2, 8px)',
          borderBottom: '1px solid var(--color-border)',
          paddingBottom: 'var(--sp-2, 8px)',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'var(--font-serif, "Source Serif Pro", serif)',
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--color-accent)',
              lineHeight: 1.2,
            }}
          >
            Design tokens · resolved
          </div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--color-text-dim)',
              marginTop: 2,
            }}
            data-testid="token-debug-count"
          >
            {tokens.length} custom properties on :root
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="token-debug-close"
          title="Close (Esc)"
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-dim)',
            borderRadius: 4,
            padding: '2px 4px',
            cursor: 'pointer',
          }}
        >
          <X style={{ width: 12, height: 12 }} />
        </button>
      </header>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
        data-testid="token-debug-table"
      >
        <tbody>
          {tokens.map((t) => {
            const isColor = _COLOR_LIKE.test(t.value);
            return (
              <tr key={t.name} style={{ verticalAlign: 'top' }}>
                <td
                  style={{
                    fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                    fontSize: 11,
                    color: 'var(--color-text)',
                    padding: '3px 6px 3px 0',
                    wordBreak: 'break-all',
                    width: '48%',
                  }}
                >
                  {t.name}
                </td>
                <td
                  style={{
                    fontFamily: 'var(--font-mono, "JetBrains Mono", monospace)',
                    fontSize: 10,
                    color: 'var(--color-text-sec)',
                    padding: '3px 0',
                    wordBreak: 'break-all',
                  }}
                >
                  {t.value}
                </td>
                <td style={{ width: 20, padding: '3px 0 3px 6px' }}>
                  {isColor && (
                    <span
                      data-testid={`token-debug-swatch-${t.name}`}
                      title={t.value}
                      style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        border: '1px solid var(--color-border)',
                        background: t.value,
                        verticalAlign: 'middle',
                      }}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </aside>
  );
}
